import React, { forwardRef } from 'react';
import { Icon } from './Icon';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Leading icon name */
  icon?: string;
  /** Trailing icon or element */
  endAdornment?: React.ReactNode;
  /** Whether the input is in error state */
  error?: boolean;
  /** Size */
  inputSize?: 'sm' | 'md' | 'lg';
}

const SIZE_CLASSES = {
  sm: 'h-9 text-xs',
  md: 'h-11 text-sm',
  lg: 'h-12 text-base',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ icon, endAdornment, error, inputSize = 'md', className = '', ...rest }, ref) => {
    return (
      <div className="relative">
        {icon && (
          <div className="absolute start-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
            <Icon name={icon} size={18} />
          </div>
        )}
        <input
          ref={ref}
          // Default autofill off so plain fields (search boxes etc.) don't
          // trigger the browser's credit-card / address autofill. Fields
          // that WANT autofill (login, password, real card inputs) pass
          // their own autoComplete, which overrides this via {...rest}.
          autoComplete="off"
          className={[
            'w-full rounded-xl border-2 bg-white dark:bg-slate-900 text-slate-900 dark:text-white',
            'placeholder:text-slate-400 dark:placeholder:text-slate-500',
            'focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all',
            'disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed',
            error
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500/10'
              : 'border-slate-200 focus:border-primary dark:border-slate-800',
            SIZE_CLASSES[inputSize],
            icon ? 'ps-10' : 'ps-4',
            endAdornment ? 'pe-12' : 'pe-4',
            className,
          ].join(' ')}
          {...rest}
        />
        {endAdornment && (
          <div className="absolute end-3 top-1/2 -translate-y-1/2 text-slate-400">
            {endAdornment}
          </div>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

// Textarea variant
interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ error, className = '', ...rest }, ref) => {
    return (
      <textarea
        ref={ref}
        className={[
          'w-full rounded-xl border-2 bg-white dark:bg-slate-900 text-slate-900 dark:text-white',
          'placeholder:text-slate-400 dark:placeholder:text-slate-500',
          'focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all',
          'disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed',
          'px-4 py-3 text-sm min-h-[100px] resize-y',
          error
            ? 'border-red-300 focus:border-red-500 focus:ring-red-500/10'
            : 'border-slate-200 focus:border-primary dark:border-slate-800',
          className,
        ].join(' ')}
        {...rest}
      />
    );
  }
);
Textarea.displayName = 'Textarea';

// Select (native wrapper — styled consistently with Input)
interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  error?: boolean;
  inputSize?: 'sm' | 'md' | 'lg';
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ error, inputSize = 'md', className = '', children, ...rest }, ref) => {
    return (
      <div className="relative">
        <select
          ref={ref}
          className={[
            'w-full rounded-xl border-2 bg-white dark:bg-slate-900 text-slate-900 dark:text-white',
            'focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all appearance-none',
            'disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed',
            'px-4 pe-10',
            error
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500/10'
              : 'border-slate-200 focus:border-primary dark:border-slate-800',
            SIZE_CLASSES[inputSize],
            className,
          ].join(' ')}
          {...rest}
        >
          {children}
        </select>
        <div className="absolute end-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
          <Icon name="expand_more" size={18} />
        </div>
      </div>
    );
  }
);
Select.displayName = 'Select';
